import * as React from 'react';
import { IDataProvider } from '../providers/IDataProvider';
import { IAuthProvider, IUser, UserRole } from '../providers/IAuthProvider';

export interface IProviders {
  data: IDataProvider;
  auth: IAuthProvider;
}

const ProvidersContext = React.createContext<IProviders | null>(null);

export interface IProvidersProviderProps {
  providers: IProviders;
  children: React.ReactNode;
}

export const ProvidersProvider: React.FC<IProvidersProviderProps> = ({ providers, children }) => {
  return (
    <ProvidersContext.Provider value={providers}>
      {children}
    </ProvidersContext.Provider>
  );
};

export function useProviders(): IProviders {
  const ctx = React.useContext(ProvidersContext);
  if (!ctx) throw new Error('useProviders must be used inside <ProvidersProvider>');
  return ctx;
}

export function useData(): IDataProvider {
  return useProviders().data;
}

export function useAuthProvider(): IAuthProvider {
  return useProviders().auth;
}

export interface IAuthState {
  user: IUser | null;
  loading: boolean;
  /** Strict role equality — `hasRole('admin')` is only true for admins. */
  hasRole: (role: UserRole) => boolean;
  /** True for any RM internal staff member (admin OR rm). False for external clients. */
  isStaff: boolean;
  /** True for external client users only. */
  isExternalClient: boolean;
  refresh: () => void;
}

export function useAuth(): IAuthState {
  const auth = useAuthProvider();
  const [user, setUser] = React.useState<IUser | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(() => {
    setLoading(true);
    auth.getCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [auth]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const hasRole = React.useCallback((role: UserRole) => {
    if (!user) return false;
    return user.role === role;
  }, [user]);

  const isStaff = !!user && (user.role === 'admin' || user.role === 'rm');
  const isExternalClient = !!user && user.role === 'client';

  return { user, loading, hasRole, isStaff, isExternalClient, refresh };
}
